import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { fetchMyOrder, fetchMyOrders, resumeOrderPayment } from '@/api/me';
import { OrderDetailModal } from '@/components/OrderDetailModal';
import { useAsyncData } from '@/hooks/useAsyncData';
const STATUS_LABEL = {
    CREATED: 'Создан',
    AWAITING_PAYMENT: 'Ожидает оплаты',
    PREPARING: 'Готовится',
    READY_FOR_PICKUP: 'Готов к выдаче',
    ON_THE_WAY: 'В пути',
    RECEIVED: 'Получен',
    PREPAIR_TO_CANCELLED: 'Отмена (обработка)',
    CANCELLED_WITH_REFUND: 'Отменён с возвратом',
    CANCELLED_WITHOUT_REFUND: 'Отменён без возврата',
};
function formatDt(iso) {
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return iso;
        return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
    }
    catch {
        return iso;
    }
}
function deliveryLabel(t) {
    return t === 'COURIER' ? 'Доставка' : t === 'PICKUP' ? 'Самовывоз' : t;
}
export function UserOrdersPage() {
    const { data: orders, loading, error: fetchError, } = useAsyncData('my-orders', () => fetchMyOrders());
    const error = fetchError ? 'Не удалось загрузить заказы' : '';
    const [selectedId, setSelectedId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [payError, setPayError] = useState('');
    const orderList = orders ?? [];
    const closeModal = useCallback(() => {
        setModalOpen(false);
        setSelectedId(null);
        setDetail(null);
        setDetailError('');
    }, []);
    const handlePay = useCallback(async (e, orderId) => {
        e.stopPropagation();
        setPayError('');
        try {
            const { redirectUrl } = await resumeOrderPayment(orderId);
            const url = redirectUrl?.trim();
            if (!url) {
                setPayError('Ссылка на оплату не получена');
                return;
            }
            window.location.assign(url);
        }
        catch {
            setPayError('Не удалось получить ссылку на оплату');
        }
    }, []);
    const openRow = useCallback((orderId) => {
        if (modalOpen && selectedId === orderId) {
            closeModal();
            return;
        }
        setSelectedId(orderId);
        setModalOpen(true);
        setDetail(null);
        setDetailError('');
        setDetailLoading(true);
        fetchMyOrder(orderId)
            .then((d) => setDetail(d))
            .catch(() => setDetailError('Не удалось загрузить заказ'))
            .finally(() => setDetailLoading(false));
    }, [modalOpen, selectedId, closeModal]);
    return (_jsxs(_Fragment, { children: [_jsx("h1", { className: "user-panel__title", children: "\u0417\u0430\u043A\u0430\u0437\u044B" }), _jsx("p", { className: "user-muted", children: "\u041D\u0430\u0436\u043C\u0438\u0442\u0435 \u043D\u0430 \u0441\u0442\u0440\u043E\u043A\u0443, \u0447\u0442\u043E\u0431\u044B \u043E\u0442\u043A\u0440\u044B\u0442\u044C \u0434\u0435\u0442\u0430\u043B\u0438 \u0437\u0430\u043A\u0430\u0437\u0430 \u0432 \u043E\u043A\u043D\u0435" }), loading ? _jsx("p", { className: "user-muted", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" }) : null, error ? (_jsx("p", { className: "user-muted", style: { color: '#991b1b' }, children: error })) : null, payError ? (_jsx("p", { className: "user-muted", style: { color: '#991b1b' }, children: payError })) : null, !loading && !error && orderList.length === 0 ? (_jsx("p", { className: "user-order-empty", children: "\u041F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0437\u0430\u043A\u0430\u0437\u043E\u0432" })) : null, !loading && orderList.length > 0 ? (_jsx("div", { className: "user-orders-table-wrap", children: _jsxs("table", { className: "user-orders-table user-orders-table--interactive", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "\u2116" }), _jsx("th", { children: "\u0414\u0430\u0442\u0430" }), _jsx("th", { children: "\u0421\u0443\u043C\u043C\u0430" }), _jsx("th", { children: "\u0421\u0442\u0430\u0442\u0443\u0441" }), _jsx("th", { children: "\u0422\u0438\u043F" }), _jsx("th", { "aria-label": "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F" })] }) }), _jsx("tbody", { children: orderList.map((o) => (_jsxs("tr", { className: modalOpen && selectedId === o.id
                                    ? 'user-order-row user-order-row--active'
                                    : 'user-order-row', onClick: () => openRow(o.id), onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openRow(o.id);
                                    }
                                }, role: "button", tabIndex: 0, "aria-expanded": modalOpen && selectedId === o.id, "aria-label": `Заказ ${o.id}, открыть детали`, children: [_jsx("td", { children: o.id }), _jsx("td", { children: formatDt(o.datetime) }), _jsxs("td", { children: [o.amount.toFixed(2), " \u20BD"] }), _jsx("td", { children: STATUS_LABEL[o.status] ?? o.status }), _jsx("td", { children: deliveryLabel(o.deliveryType) }), _jsx("td", { onClick: (e) => e.stopPropagation(), children: o.status === 'AWAITING_PAYMENT' ? (_jsx("button", { type: "button", className: "user-order-pay-btn", onClick: (e) => void handlePay(e, o.id), children: "\u041E\u043F\u043B\u0430\u0442\u0438\u0442\u044C" })) : null })] }, o.id))) })] }) })) : null, _jsx(OrderDetailModal, { open: modalOpen, onClose: closeModal, loading: detailLoading, error: detailError, detail: detail })] }));
}
